<template>
  <div class="highlight-picker-dropdown" ref="dropdown">
    <button
      class="highlight-picker-button"
      @click="toggleDropdown"
      :title="title"
      type="button"
    >
      <span class="bbl-highlight-icon" :style="{ backgroundColor: currentColor || 'transparent' }">H</span>
      <span class="dropdown-arrow">▼</span>
    </button>
    
    <div v-if="isOpen" class="highlight-dropdown-panel">
      <div class="color-section">
        <button
          class="color-action-button"
          @click="removeHighlight"
          type="button"
        >
          <span class="remove-icon">✕</span> Remove Highlight
        </button>
      </div>
      
      <div class="color-section">
        <div class="section-label">Highlight Colors</div>
        <div class="color-grid">
          <button
            v-for="color in highlightColors"
            :key="color.value"
            class="color-swatch"
            :style="{ backgroundColor: color.value }"
            :title="color.name"
            @click="selectColor(color.value)"
            type="button"
          >
            <span v-if="currentColor === color.value" class="check-mark">✓</span>
          </button>
        </div>
      </div>
      
      <div class="color-section">
        <div class="section-label">More Colors</div>
        <div class="color-grid">
          <button
            v-for="color in moreColors"
            :key="color.value"
            class="color-swatch"
            :style="{ backgroundColor: color.value }"
            :title="color.name"
            @click="selectColor(color.value)"
            type="button"
          >
            <span v-if="currentColor === color.value" class="check-mark">✓</span>
          </button>
        </div>
      </div>
      
      <div class="color-section">
        <div class="section-label">Custom Color</div>
        <div class="custom-color-picker">
          <input
            type="color"
            v-model="customColor"
            @change="selectColor(customColor)"
            class="color-input"
          />
          <input
            type="text"
            v-model="customColor"
            @keyup.enter="selectColor(customColor)"
            placeholder="#FFFF00"
            class="color-text-input"
            maxlength="7"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HighlightPickerDropdown',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: 'Highlight Color'
    }
  },
  data() {
    return {
      isOpen: false,
      customColor: '#FFFF00',
      highlightColors: [
        { name: 'Yellow', value: '#FFFF00' },
        { name: 'Light Yellow', value: '#FFFF99' },
        { name: 'Light Green', value: '#CCFFCC' },
        { name: 'Light Blue', value: '#CCFFFF' },
        { name: 'Light Pink', value: '#FFCCFF' },
        { name: 'Light Orange', value: '#FFCC99' }
      ],
      moreColors: [
        { name: 'Bright Yellow', value: '#FFF59D' },
        { name: 'Lime', value: '#E6FF99' },
        { name: 'Mint', value: '#CCFFE6' },
        { name: 'Cyan', value: '#99FFFF' },
        { name: 'Sky Blue', value: '#CCE5FF' },
        { name: 'Lavender', value: '#E6CCFF' },
        { name: 'Pink', value: '#FFCCE6' },
        { name: 'Peach', value: '#FFE6CC' },
        { name: 'Light Coral', value: '#FFCCCC' },
        { name: 'Light Gray', value: '#E0E0E0' },
        { name: 'Beige', value: '#F5F5DC' },
        { name: 'Light Salmon', value: '#FFE4B5' }
      ]
    }
  },
  computed: {
    currentColor() {
      return this.modelValue
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen
    },
    selectColor(color) {
      this.$emit('change', color)
      this.isOpen = false
    },
    removeHighlight() {
      this.$emit('remove')
      this.isOpen = false
    },
    handleClickOutside(event) {
      if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
        this.isOpen = false
      }
    }
  }
}
</script>

<style scoped>
.highlight-picker-dropdown {
  position: relative;
  display: inline-block;
}

.highlight-picker-button {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.highlight-picker-button:hover {
  background: #f5f5f5;
  border-color: #999;
}

.bbl-highlight-icon {
  font-weight: bold;
  font-size: 17px;
  padding: 0px 4px;
  border-radius: 2px;
  border: 1px solid #ddd;
}

.dropdown-arrow {
  font-size: 8px;
  color: #666;
}

.highlight-dropdown-panel {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 8px;
  z-index: 1000;
  min-width: 220px;
}

.color-section {
  margin-bottom: 12px;
}

.color-section:last-child {
  margin-bottom: 0;
}

.section-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 6px;
  font-weight: 500;
}

.color-action-button {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.color-action-button:hover {
  background: #f5f5f5;
  border-color: #999;
}

.remove-icon {
  color: #e74c3c;
  font-weight: bold;
}

.color-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 4px;
}

.color-swatch {
  width: 28px;
  height: 28px;
  border: 1px solid #ddd;
  border-radius: 3px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
}

.color-swatch:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 1;
}

.check-mark {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #333;
  font-weight: bold;
  font-size: 14px;
  text-shadow: 0 0 2px rgba(255, 255, 255, 0.8);
}

.custom-color-picker {
  display: flex;
  gap: 6px;
  align-items: center;
}

.color-input {
  width: 40px;
  height: 32px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
}

.color-text-input {
  flex: 1;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  font-family: monospace;
}

.color-text-input:focus {
  outline: none;
  border-color: #3498db;
}
</style>
